import { AsyncPipe, NgIf } from '@angular/common';
import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ConnectionStatus } from '@capacitor/network';
import { BoondockFacade } from '@curbnturf/boondock-listing';
import { Boondock, HelperFunctions, User } from '@curbnturf/entities';
import { RatingFacade } from '@curbnturf/rating';
import * as RatingActions from '@curbnturf/rating/src/lib/+state/rating.actions';
import { StatusFacade } from '@curbnturf/status';
import { UserFacade } from '@curbnturf/user/src/lib/+state/user.facade';
import { IonicModule, LoadingController, NavController } from '@ionic/angular';
import { Actions, ofType } from '@ngrx/effects';
import { Observable, Subscription } from 'rxjs';

@Component({
  selector: 'curbnturf-rating-boondock-caretaker',
  templateUrl: './rating-boondock-caretaker.component.html',
  styleUrls: ['./rating-boondock-caretaker.component.scss'],
  standalone: true,
  imports: [NgIf, IonicModule, AsyncPipe],
})
export class RatingBoondockCaretakerComponent implements OnInit, OnDestroy {
  @Output() becomeCaretaker: EventEmitter<void> = new EventEmitter<void>();

  @Output() skip: EventEmitter<void> = new EventEmitter<void>();

  @Output() navigateBack: EventEmitter<void> = new EventEmitter<void>();

  subscriptions: Subscription[] = [];

  boondock$: Observable<Boondock | null>;
  boondockId: number | undefined;
  currentUser$: Observable<User>;
  currentUser: User;
  networkStatus: ConnectionStatus | undefined;

  getAvatarPhoto = HelperFunctions.getPhotoUrlSquareMedium;

  appContext: boolean;

  get isOnline(): boolean {
    if (this.networkStatus) {
      return !!this.networkStatus?.connected;
    } else {
      return true;
    }
  }

  constructor(
    private updates$: Actions,
    private route: ActivatedRoute,
    private loadingController: LoadingController,
    private navCtrl: NavController,
    private ratingFacade: RatingFacade,
    private boondockFacade: BoondockFacade,
    private statusFacade: StatusFacade,
    private userFacade: UserFacade,
  ) {}

  ngOnInit(): void {
    this.currentUser$ = this.userFacade.currentUser$;
    this.route.data.subscribe((data: { appContext: boolean }) => {
      this.appContext = data.appContext || false;
    });

    this.boondock$ = this.boondockFacade.selected$;
    this.subscriptions.push(
      this.boondock$.subscribe((boondock) => {
        if (boondock) {
          this.boondockId = boondock.id;
        }
      }),
      this.statusFacade.networkStatus$.subscribe((status) => {
        this.networkStatus = status;
      }),
      this.userFacade.currentUser$.subscribe((user) => {
        this.currentUser = user;
      }),
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => {
      if (!sub.closed) {
        sub.unsubscribe();
      }
    });
  }

  async onBecomeCaretaker() {
    const loading = await this.loadingController.create({
      message: 'Saving...',
      duration: 2000,
    });
    await loading.present();

    // Make the subscription before sending the request to make sure we don't miss anything.
    this.subscriptions.push(
      this.updates$.pipe(ofType(RatingActions.selectCaretakerBoondock)).subscribe(async () => {
        await loading.dismiss();
        // Emit event for web and navigate for app.
        if (this.appContext) {
          await this.navCtrl.navigateForward(['rating', 'boondock', this.boondockId, 'sections']);
        } else {
          this.becomeCaretaker.emit();
        }
      }),
    );

    this.ratingFacade.selectCaretakerBoondock({
      requestingTakeover: true,
      boondockId: this.boondockId,
      boondock: {
        id: this.boondockId,
      },
    });
  }

  async onSkip() {
    // Emit event for web and navigate for app.
    if (this.appContext) {
      await this.navCtrl.navigateForward(['rating', 'boondock', this.boondockId, 'sections']);
    } else {
      this.skip.emit();
    }
  }
}
